/**
 * IntroModelSlide - Slide 1
 *
 * Introduction text and base-model selection grid.
 *
 * Mock mode renders the original 4-entry hardcoded `MODELS` array.
 * Real mode (when `realModeBaseModels` is supplied) renders the live
 * catalog returned by `useLoraBaseModels()`. In v1 the live catalog
 * has exactly one row (`flux-dev`), so the grid collapses to a single
 * card and selection is locked.
 */

import { ModelCard } from "../components/ModelCard";
import { MODELS, type ModelId, type ModelInfo } from "../types";
import type { LoraBaseModel } from "@/types/loras";

interface IntroModelSlideProps {
  selectedModel: ModelId | string | null;
  onSelectModel: (modelId: ModelId | string) => void;
  /** When provided, switch to real-mode catalog rendering. */
  realModeBaseModels?: LoraBaseModel[];
  /** When true, selection is disabled (real-mode pre-pins the model). */
  realModeLocked?: boolean;
}

function backendToModelInfo(model: LoraBaseModel): ModelInfo {
  // Only SRPO model should be marked as recommended
  const isSrpo = model.slug.toLowerCase().includes("srpo");
  return {
    id: model.slug as ModelId,
    name: model.displayName,
    vendor: model.providerSlug ?? "—",
    fidelity: isSrpo ? 4.5 : 4,
    speed: 3,
    vram: 3,
    license: "—",
    isRecommended: isSrpo,
    bullets: [
      `Output media: ${model.outputMediaType}`,
      model.isAvailable === false ? "Not yet available" : "Ready for training",
    ],
  };
}

export function IntroModelSlide({
  selectedModel,
  onSelectModel,
  realModeBaseModels,
  realModeLocked,
}: IntroModelSlideProps) {
  const realMode = realModeBaseModels !== undefined;
  const models: ModelInfo[] = realMode
    ? realModeBaseModels.map(backendToModelInfo)
    : MODELS;

  return (
    <div>
      <div className="mb-8">
        <h2 className="text-2xl font-semibold text-foreground mb-3">
          Train a custom LoRA
        </h2>
        <p className="text-sm text-muted-foreground max-w-xl leading-relaxed">
          A LoRA (Low-Rank Adaptation) is a lightweight model fine-tuned on your
          reference images. Once trained, it captures the visual identity of
          this character and can be applied to any shot that references it.
        </p>
      </div>

      <div className="mb-4">
        <h3 className="text-xs text-muted-foreground uppercase tracking-wider mb-4">
          {realMode ? "Base model" : "Choose a base model"}
        </h3>
      </div>

      {models.length === 0 ? (
        <div className="rounded-lg border border-white/10 bg-[#1a1a1a] p-6 max-w-3xl">
          <p className="text-sm text-muted-foreground">
            No base models are available yet. Contact your administrator if you
            expected to see options here.
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-4 max-w-3xl">
          {models.map((model) => (
            <ModelCard
              key={model.id}
              model={model}
              isSelected={selectedModel === model.id}
              onSelect={() => {
                if (!realModeLocked) onSelectModel(model.id);
              }}
            />
          ))}
        </div>
      )}

      {!realMode && (
        <p className="text-xs text-muted-foreground mt-6 max-w-xl">
          Not sure which to pick?{" "}
          <span className="text-primary font-medium">FLUX 1 Dev</span> offers
          the best balance of quality and training speed for most character
          LoRAs.
        </p>
      )}
      {realMode && !realModeLocked && (
        <p className="text-xs text-muted-foreground mt-6 max-w-xl">
          Select a base model for training. Once training starts, the base model
          cannot be changed.
        </p>
      )}
      {realMode && realModeLocked && (
        <p className="text-xs text-muted-foreground mt-6 max-w-xl">
          The base model cannot be changed after training has started. Create a
          new character if you need a different base model.
        </p>
      )}
    </div>
  );
}
